export default {
  portal: {
    features: [
      // ========================= //
      // BOOKING FORMS
      // ========================= //
      {
        textColor: 'text-purple-500',
        bgColor: 'bg-purple-500',
        borderColor: 'border-purple-500',
        gradientColor: 'from-purple-600 to-purple-400',
        icon: 'clipboard-check',
        name: 'Booking Forms',
        summary: 'Collect orders directly from customers online with configurable white-label booking forms.',
        prefix: '',
        title: 'Collect Orders Directly From Your Customers',
        suffix: 'Effortlessly',
        lead: 'Streamline order collection with a branded online booking form. Easily configure the booking options to match your processes. Link to it directly or embed into your existing website.',
        route: 'FeatureBookingForms',
        primaryImage: 'images/features/booking-forms.png',
        secondaryImage: 'images/features/booking-summary.png',
        sections: [
          {
            image: '/images/ui/booking-origin.png',
            heading: 'Flexible and Configurable',
            body: 'Set up your booking form with settings, constraints and rules that match your operation.',
            points: [
              `Set up to collect service orders, pickups and/or deliveries.`,
              `Limit days and times based on schedules and rules.`,
              `Enter customised terms and help text based on your operations.`
            ],
          },
          {
            image: '/images/ui/booking-origin.png',
            heading: 'Make It Your Own',
            body: `Keep things consistent by applying your company's brand identity to the interface.`,
            points: [
              `Upload or add your business's logo or brandmark.`,
              `Change the colour of the buttons and accents to match your brand.`,
              `Embed it directly on your website to make it feel like yours.`
            ],
          },
          {
            image: '/images/ui/booking-origin.png',
            heading: 'User-Friendly',
            body: 'Delight your customers with a booking form that is easy to use anywhere.',
            points: [
              `Each form is mobile-friendly and works on any device.`,
              `Easy address entry with address book and autocomplete by Google Maps.`,
              `Booking confirmations are automatically sent via email.`
            ]
          }
        ]
      },
      // ========================= //
      // CUSTOMER PORTAL
      // ========================= //
      {
        textColor: 'text-violet-500',
        bgColor: 'bg-violet-500',
        borderColor: 'border-violet-500',
        gradientColor: 'from-violet-600 to-violet-400',
        icon: 'portal-enter',
        name: 'Customer Portal',
        summary: 'Give customers access to their order history and address book via a branded portal.',
        prefix: '',
        title: 'Give Customers Access to their Own',
        suffix: 'Management Dashboard',
        lead: 'Allow customers to create their own secure account, complete with their order history and address book, making it easy for them to re-order from you.',
        route: 'FeatureCustomerPortal',
        primaryImage: 'images/features/customer-portal.png',
        secondaryImage: '',
        sections: [
          {
            image: '/images/ui/booking-origin.png',
            heading: `Order History`,
            body: `Save time answering rudimentary questions by giving your customers unparalleled transparency into their history with your business.`,
            points: [
              `Customers can always see a full up-to-date history of their orders.`,
              `Each order displays its current status as well as a timeline of updates.`,
            ]
          },
          {
            image: '/images/ui/booking-origin.png',
            heading: `Easy Repeat Orders`,
            body: `Convenience is key to getting repeat business from customers. With their own account, ordering again is easy.`,
            points: [
              `Customer profile information is stored and automatically applied on future orders when logged in.`,
              `An address book is kept which can be used to quickly enter an address into a booking form.`,
            ]
          },
          {
            image: '/images/ui/booking-origin.png',
            heading: `Security Built-In`,
            body: `Privacy and security is crucial. We will never sell or use your customer's data for malicious purposes in any way.`,
            points: [
              `All accounts require a strong password which is stored with strong encryption.`,
              `Portal domains have a 256-bit SSL certificate and all data is transmitted securely over HTTPS.`,
              `Data is stored encrypted-at-rest in GDPR-compliant server regions within the EU backed by Amazon Web Services.`
            ]
          }
        ]
      },
      // ========================= //
      // LIVE STATUS TRACKING
      // ========================= //
      {
        textColor: 'text-indigo-500',
        bgColor: 'bg-indigo-500',
        borderColor: 'border-indigo-500',
        gradientColor: 'from-indigo-600 to-indigo-400',
        icon: 'location-circle',
        name: 'Live Status Tracking',
        summary: 'Display the live status of their order from start to finish with a branded status page.',
        prefix: '',
        title: 'Track the Status of an Order',
        suffix: 'In Real Time',
        lead: 'Each order has a unique tracking link where customers can view the status of their order, or track it live on an interactive map once it is in progress.',
        route: 'FeatureTracking',
        primaryImage: 'images/features/status-tracking.png',
        secondaryImage: '',
        sections: [
          {
            image: '/images/ui/booking-origin.png',
            heading: `Dedicated Online Status Page`,
            body: `All order details and the current status are displayed on a dedicated the customer can access online.`,
            points: [
              `Tracking links are sent along with the notification email when the order is approved.`,
              `All relevant order information is displayed in a mobile-friendly interface.`,
              `Avoid arriving at the wrong address by allowing the recipient to update the address with pin-point accuracy on a map.`
            ]
          },
          {
            image: '/images/ui/booking-origin.png',
            heading: `Live Driver Tracking`,
            body: `Once an order is started, the customer will be notified with an ETA and a link to track the driver in real time on an interactive map.`,
            points: [
              `Let your customers send your driver a link directly to their navigation device, keeping their phone number private.`
            ]
          },
          {
            image: '/images/ui/booking-origin.png',
            heading: `After-Service Feedback`,
            body: `Get insight into your customer service levels with customer feedback on each order.`,
            points: [
              `Ask customers for a basic 1 to 5 star rating and optionally any feedback based on their experience.`,
              `Automatically send negative feedback to your customer service team so you can act on it instantly.`
            ]
          }
        ]
      },
      // ========================= //
      // ORDER UPDATES
      // ========================= //
      {
        textColor: 'text-blue-500',
        bgColor: 'bg-blue-500',
        borderColor: 'border-blue-500',
        gradientColor: 'from-blue-600 to-blue-400',
        icon: 'bell-on',
        name: 'Order Updates',
        summary: 'Send customers updates on their orders as they happen directly to their phone and email inbox.',
        prefix: '',
        title: 'Send Order Updates As They Happen',
        suffix: 'Automatically',
        lead: 'Manage expectations and reduce customer support overhead with automatic updates - like ETAs and PODs - sent to the relevant party throughout the lifecycle of an order.',
        route: 'FeatureNotifications',
        primaryImage: 'images/features/real-time-notifications.png',
        secondaryImage: '',
        sections: [
          {
            image: '/images/ui/booking-origin.png',
            heading: `Order Status Updates`,
            body: ``,
            points: [
              ``,
              ``,
              ``
            ]
          },
          {
            image: '/images/ui/booking-origin.png',
            heading: `Accurate ETA Messages`,
            body: ``,
            points: [
              ``,
              ``,
              ``
            ]
          },
          {
            image: '/images/ui/booking-origin.png',
            heading: `Automated PODs`,
            body: ``,
            points: [
              ``,
              ``,
              ``
            ]
          }
        ]
      },
      // ========================= //
      // OPTIMISED ROUTE PLANNING
      // ========================= //
      {
        textColor: 'text-sky-500',
        bgColor: 'bg-sky-500',
        borderColor: 'border-sky-500',
        gradientColor: 'from-sky-600 to-sky-400',
        icon: 'route',
        name: 'Optimised Route Planning',
        summary: `Generate optimised routes at the click of a button and send them directly to your drivers.`,
        prefix: '',
        title: 'Manage Risk and Save Time and Money With',
        suffix: '24/7 Automated Fleet Monitoring',
        lead: 'An AI-based system that continuously monitors your fleet telematics data and triggers actions and workflows based on your specific configuration settings.',
        route: 'FeatureMonitoring',
        primaryImage: 'images/features/monitoring.png',
        secondaryImage: '',
      },
      // ========================= //
      // DRIVER APP
      // ========================= //
      {
        textColor: 'text-cyan-500',
        bgColor: 'bg-cyan-500',
        borderColor: 'border-cyan-500',
        gradientColor: 'from-cyan-600 to-cyan-400',
        icon: 'mobile',
        name: 'Driver App',
        summary: `Give drivers the tools to receive, navigate to, and complete orders with their own app.`,
        prefix: '',
        title: 'Manage Risk and Save Time and Money With',
        suffix: '24/7 Automated Fleet Monitoring',
        lead: 'An AI-based system that continuously monitors your fleet telematics data and triggers actions and workflows based on your specific configuration settings.',
        route: 'FeatureMonitoring',
        primaryImage: 'images/features/monitoring.png',
        secondaryImage: '',
      },
      // ========================= //
      // FEDEREATED DELIVERY NETWORK
      // ========================= //
      // {
      //   textColor: 'text-cyan-500',
      //   bgColor: 'bg-cyan-500',
      //   borderColor: 'border-cyan-500',
      //   gradientColor: 'from-cyan-600 to-cyan-400',
      //   icon: 'chart-network',
      //   name: 'Federated Delivery Network',
      //   summary: `Save time and money with 24/7 automated fleet monitoring based on your fleet's telematics.`,
      //   prefix: '',
      //   title: 'Manage Risk and Save Time and Money With',
      //   suffix: '24/7 Automated Fleet Monitoring',
      //   lead: 'An AI-based system that continuously monitors your fleet telematics data and triggers actions and workflows based on your specific configuration settings.',
      //   route: 'FeatureMonitoring',
      //   primaryImage: 'images/features/monitoring.png',
      //   secondaryImage: '',
      // }
    ]
  },
  radar: {
    features: [
      // ========================= //
      // ALWAWYS-ON MONITORING
      // ========================= //
      {
        textColor: 'text-teal-500',
        bgColor: 'bg-teal-500',
        borderColor: 'border-teal-500',
        gradientColor: 'from-teal-600 to-teal-400',
        icon: 'radar',
        name: 'Always-On Monitoring',
        summary: `Save time and money with 24/7 automated fleet monitoring based on your fleet's telematics.`,
        prefix: '',
        title: 'Manage Risk and Save Time and Money With',
        suffix: '24/7 Automated Fleet Monitoring',
        lead: 'An AI-based system that continuously monitors your fleet telematics data and triggers actions and workflows based on your specific configuration settings.',
        route: 'FeatureMonitoring',
        primaryImage: 'images/features/monitoring.png',
        secondaryImage: '',
        sections: [
          {
            heading: 'Reduce Your Monthly Costs & Increase Vehicle Longevity',
            body: `You shouldn't be spending money on things you don't have to. Bring down your expenses on fuel, fines, and compliance.`,
            image: '/images/ui/automation-form.png',
            points: [
              'Fewer Speeding Incidents & Fines: Automatically detect vehicles exceeding road or fleet speed limits and warn them via their driver terminal as it happens.',
              'Decrease Fuel Spent on Excessive Idling: Set a time limit for idling with the engine on and automatically prompt the driver to switch off when that time is exceeded.',
              'Fewer Harsh Driving Incidents: Whenever severe acceleration, braking, or cornering happens, immediately warn the driver to stop via their driver terminal.'
            ]
          },
          {
            heading: 'Coach Your Drivers Into Safer & Smarter Driving Behaviour',
            body: 'Drivers are the linchpin of your organisation. Invest in improving their behaviour and promote responsibility with automated coaching prompts.',
            image: '/images/ui/login-reminder.png',
            points: [
              'Remind Drivers to Login & Start Work: Improve the quality of your WEBFLEET data and make sure drivers stay accountable by reminding them log into their device and set their work status.',
              `Manage Fatigue & Rest Periods: Promote driver health and safety with automatic prompts to the driver to pull over and rest for a specified period of time.`,
              'Actively Send Disciplinary Messages: Whenever unwanted driving behaviour is detected, send drivers custom personalised messages to remind them about proper driving etiquette.'
            ]
          },
          {
            heading: 'Actively Manage Risk With Realtime Alerts & Notifications',
            body: 'Detect potential problems as they happen notify the relevant person immediately.',
            image: '/images/ui/monitor-alerts.png',
            points: [
              'When Vehicles Enter/Leave High-Risk Areas: Setup geofenced areas in WEBFLEET and receive SMS or email notifications whenever defined vehicles enter or leave those areas.',
              `Sudden Drops in Fuel Level: Investigate anomalies in fuel levels immediately. Avoid fuel theft, and ensure drivers aren't left stranded.`,
              'Panic Buttons & Tampering Detection: Install a panic button in vehicles for drivers to alert the control room of an incident. Automatically be alerted of potential device tampering.'
            ]
          }
        ]
      },
      // ========================= //
      // AUTOMATIC ALERTS
      // ========================= //
      {
        textColor: 'text-lime-500',
        bgColor: 'bg-lime-500',
        borderColor: 'border-lime-500',
        gradientColor: 'from-lime-600 to-lime-400',
        icon: 'exclamation-circle',
        name: 'Automatic Alerts',
        summary: `Track your fleet's expenses and gain insight into cost centres with detailed reports.`,
        prefix: '',
        title: 'Track Your Fleet Expenses and',
        suffix: 'Gain Insight Into Cost Centres',
        lead: 'The simplest way to track all your fleet-related expenses, with detailed cost reporting and file storage - creating a digital paper trail per vehicle.',
        route: 'FeatureRecords',
        primaryImage: 'images/features/records.png',
        secondaryImage: '',
        sections: [
          {
            heading: 'Record All Your Fleet’s Expenses in One Central Place',
            body: 'Maintain an easily auditable log of all your fleet’s expenses with just a single form.',
            image: '/images/ui/record-form.png',
            points: [
              'Capture any kind of expense. Fuel, repairs, services, tyres, fines, tolls, insurance premiums, or anything you can think of.',
              'Look up any record from the past with ease. Find exactly what you’re looking for by filtering your records by date, vehicle, vehicle group, category, vendor, amount etc.'
            ]
          },
          {
            heading: 'Go Paperless. Attach and Keep Digital Copies of Files. Forever.',
            body: `Store digital copies of absolutely anything you'd need to keep a record of:`,
            image: '/images/ui/record-files.png',
            points: [
              'Income and Expenses: Keep a tally of those receipts, invoices and purchase orders.',
              'Legal Documents: Upload those signed contracts, certificates of ownership and other important documentation for safe-keeping.',
              'Auditable Evidence: Save trip and work log books, or photographs of damage for insurance claims.'
            ]
          },
          {
            heading: 'Detailed Analytics and Reports with Just One Click',
            body: 'Get a complete month-by-month overview of all your fleet’s expenses in an Excel report from a single button.',
            image: '/images/ui/record-stats.png',
            points: [
              'Cost Per Kilometre: Figure out which vehicles are earning more per unit of work than the others, and which ones are costing you more.',
              'Total Expense Breakdowns: Get expense breakdowns by vehicle, group, category, and vendor for any given month or year.',
              'Key Metrics with Aggregated Charts: Spot trends in your data with easy to read charts and data visualisations that continuously update based on your latest telemetry data and records.'
            ]
          }
        ]
      },
      // ========================= //
      // FLEET RECORDS
      // ========================= //
      {
        textColor: 'text-emerald-500',
        bgColor: 'bg-emerald-500',
        borderColor: 'border-emerald-500',
        gradientColor: 'from-emerald-600 to-emerald-400',
        icon: 'receipt',
        name: 'Fleet Records',
        summary: `Track your fleet's expenses and gain insight into cost centres with detailed reports.`,
        prefix: '',
        title: 'Track Your Fleet Expenses and',
        suffix: 'Gain Insight Into Cost Centres',
        lead: 'The simplest way to track all your fleet-related expenses, with detailed cost reporting and file storage - creating a digital paper trail per vehicle.',
        route: 'FeatureRecords',
        primaryImage: 'images/features/records.png',
        secondaryImage: '',
        sections: [
          {
            heading: 'Record All Your Fleet’s Expenses in One Central Place',
            body: 'Maintain an easily auditable log of all your fleet’s expenses with just a single form.',
            image: '/images/ui/record-form.png',
            points: [
              'Capture any kind of expense. Fuel, repairs, services, tyres, fines, tolls, insurance premiums, or anything you can think of.',
              'Look up any record from the past with ease. Find exactly what you’re looking for by filtering your records by date, vehicle, vehicle group, category, vendor, amount etc.'
            ]
          },
          {
            heading: 'Go Paperless. Attach and Keep Digital Copies of Files. Forever.',
            body: `Store digital copies of absolutely anything you'd need to keep a record of:`,
            image: '/images/ui/record-files.png',
            points: [
              'Income and Expenses: Keep a tally of those receipts, invoices and purchase orders.',
              'Legal Documents: Upload those signed contracts, certificates of ownership and other important documentation for safe-keeping.',
              'Auditable Evidence: Save trip and work log books, or photographs of damage for insurance claims.'
            ]
          },
          {
            heading: 'Detailed Analytics and Reports with Just One Click',
            body: 'Get a complete month-by-month overview of all your fleet’s expenses in an Excel report from a single button.',
            image: '/images/ui/record-stats.png',
            points: [
              'Cost Per Kilometre: Figure out which vehicles are earning more per unit of work than the others, and which ones are costing you more.',
              'Total Expense Breakdowns: Get expense breakdowns by vehicle, group, category, and vendor for any given month or year.',
              'Key Metrics with Aggregated Charts: Spot trends in your data with easy to read charts and data visualisations that continuously update based on your latest telemetry data and records.'
            ]
          }
        ]
      },
      // ========================= //
      // CUSTOMISED REPORTING
      // ========================= //
      {
        textColor: 'text-green-500',
        bgColor: 'bg-green-500',
        borderColor: 'border-green-500',
        gradientColor: 'from-green-600 to-green-400',
        icon: 'chart-pie',
        name: 'Customised Reporting',
        summary: `Track your fleet's expenses and gain insight into cost centres with detailed reports.`,
        prefix: '',
        title: 'Track Your Fleet Expenses and',
        suffix: 'Gain Insight Into Cost Centres',
        lead: 'The simplest way to track all your fleet-related expenses, with detailed cost reporting and file storage - creating a digital paper trail per vehicle.',
        route: 'FeatureRecords',
        primaryImage: 'images/features/records.png',
        secondaryImage: '',
        sections: [
          {
            heading: 'Record All Your Fleet’s Expenses in One Central Place',
            body: 'Maintain an easily auditable log of all your fleet’s expenses with just a single form.',
            image: '/images/ui/record-form.png',
            points: [
              'Capture any kind of expense. Fuel, repairs, services, tyres, fines, tolls, insurance premiums, or anything you can think of.',
              'Look up any record from the past with ease. Find exactly what you’re looking for by filtering your records by date, vehicle, vehicle group, category, vendor, amount etc.'
            ]
          },
          {
            heading: 'Go Paperless. Attach and Keep Digital Copies of Files. Forever.',
            body: `Store digital copies of absolutely anything you'd need to keep a record of:`,
            image: '/images/ui/record-files.png',
            points: [
              'Income and Expenses: Keep a tally of those receipts, invoices and purchase orders.',
              'Legal Documents: Upload those signed contracts, certificates of ownership and other important documentation for safe-keeping.',
              'Auditable Evidence: Save trip and work log books, or photographs of damage for insurance claims.'
            ]
          },
          {
            heading: 'Detailed Analytics and Reports with Just One Click',
            body: 'Get a complete month-by-month overview of all your fleet’s expenses in an Excel report from a single button.',
            image: '/images/ui/record-stats.png',
            points: [
              'Cost Per Kilometre: Figure out which vehicles are earning more per unit of work than the others, and which ones are costing you more.',
              'Total Expense Breakdowns: Get expense breakdowns by vehicle, group, category, and vendor for any given month or year.',
              'Key Metrics with Aggregated Charts: Spot trends in your data with easy to read charts and data visualisations that continuously update based on your latest telemetry data and records.'
            ]
          }
        ]
      },
      // ========================= //
      // TELEMETRY INTEGRATIONS
      // ========================= //
      {
        textColor: 'text-lime-500',
        bgColor: 'bg-lime-500',
        borderColor: 'border-lime-500',
        gradientColor: 'from-lime-600 to-lime-400',
        icon: 'plug',
        name: 'Telemetry Integrations',
        summary: `Track your fleet's expenses and gain insight into cost centres with detailed reports.`,
        prefix: '',
        title: 'Track Your Fleet Expenses and',
        suffix: 'Gain Insight Into Cost Centres',
        lead: 'The simplest way to track all your fleet-related expenses, with detailed cost reporting and file storage - creating a digital paper trail per vehicle.',
        route: 'FeatureRecords',
        primaryImage: 'images/features/records.png',
        secondaryImage: '',
        sections: [
          {
            heading: 'Record All Your Fleet’s Expenses in One Central Place',
            body: 'Maintain an easily auditable log of all your fleet’s expenses with just a single form.',
            image: '/images/ui/record-form.png',
            points: [
              'Capture any kind of expense. Fuel, repairs, services, tyres, fines, tolls, insurance premiums, or anything you can think of.',
              'Look up any record from the past with ease. Find exactly what you’re looking for by filtering your records by date, vehicle, vehicle group, category, vendor, amount etc.'
            ]
          },
          {
            heading: 'Go Paperless. Attach and Keep Digital Copies of Files. Forever.',
            body: `Store digital copies of absolutely anything you'd need to keep a record of:`,
            image: '/images/ui/record-files.png',
            points: [
              'Income and Expenses: Keep a tally of those receipts, invoices and purchase orders.',
              'Legal Documents: Upload those signed contracts, certificates of ownership and other important documentation for safe-keeping.',
              'Auditable Evidence: Save trip and work log books, or photographs of damage for insurance claims.'
            ]
          },
          {
            heading: 'Detailed Analytics and Reports with Just One Click',
            body: 'Get a complete month-by-month overview of all your fleet’s expenses in an Excel report from a single button.',
            image: '/images/ui/record-stats.png',
            points: [
              'Cost Per Kilometre: Figure out which vehicles are earning more per unit of work than the others, and which ones are costing you more.',
              'Total Expense Breakdowns: Get expense breakdowns by vehicle, group, category, and vendor for any given month or year.',
              'Key Metrics with Aggregated Charts: Spot trends in your data with easy to read charts and data visualisations that continuously update based on your latest telemetry data and records.'
            ]
          }
        ]
      }
    ]
  }
}